import { UNIT_ENGAGEMENT_CONFIG } from './config.js';
/**
 * The engagement state machine shared by web and mobile.
 *
 * Deliberately free of platform APIs: callers translate their own lifecycle
 * (`visibilitychange`, `AppState`, navigation focus) into the handful of calls
 * below, so both platforms end up with identical semantics — one
 * `page_inactive` per visit, a fresh `visit_id` per view, and interactions
 * throttled into at most one `page_active` per window. Arrival is the one
 * report exempt from that throttle.
 */
export function createUnitVisitTracker(options) {
    const config = { ...UNIT_ENGAGEMENT_CONFIG, ...options.config };
    const now = options.now ?? (() => Date.now());
    let visitId = null;
    let visitEnded = false;
    let visitStartedAt = 0;
    let lastHeartbeatAt = 0;
    let isVisible = false;
    let hiddenAt = null;
    let idleTimer = null;
    let graceTimer = null;
    function clearIdleTimer() {
        if (idleTimer !== null) {
            clearTimeout(idleTimer);
            idleTimer = null;
        }
    }
    function clearGraceTimer() {
        if (graceTimer !== null) {
            clearTimeout(graceTimer);
            graceTimer = null;
        }
    }
    function armIdleTimer() {
        clearIdleTimer();
        idleTimer = setTimeout(() => end(), config.idleMs);
    }
    function start() {
        if (visitId !== null && !visitEnded)
            return;
        visitId = options.createVisitId();
        visitEnded = false;
        isVisible = true;
        visitStartedAt = now();
        // Seeded from the start of the visit rather than zero so the first
        // interaction doesn't fire a heartbeat seconds after the arrival report
        // already said the user was active.
        lastHeartbeatAt = visitStartedAt;
        options.onActive({ visitId });
        armIdleTimer();
    }
    function registerInteraction() {
        if (visitId === null || visitEnded)
            return;
        armIdleTimer();
        const timestamp = now();
        if (timestamp - visitStartedAt >= config.maxVisitMs)
            return;
        if (timestamp - lastHeartbeatAt < config.heartbeatMs)
            return;
        lastHeartbeatAt = timestamp;
        options.onActive({ visitId });
    }
    function hide() {
        if (visitId === null || visitEnded || !isVisible)
            return;
        isVisible = false;
        hiddenAt = now();
        // The idle timer keeps running: a sub-grace bounce doesn't reset it.
        clearGraceTimer();
        graceTimer = setTimeout(() => end(), config.backgroundGraceMs);
    }
    function show() {
        if (visitId === null)
            return;
        clearGraceTimer();
        const hiddenFor = hiddenAt === null ? 0 : now() - hiddenAt;
        hiddenAt = null;
        if (visitEnded) {
            // The previous visit already ended, so this is a new one.
            start();
            return;
        }
        // The timer above can't be trusted to have run: iOS suspends the JS thread
        // within seconds of backgrounding, and browsers throttle or freeze timers
        // in hidden tabs, so a long absence can come back with the timer still
        // pending. Elapsed wall-clock time is the same measurement without
        // depending on whether anything was executing.
        if (hiddenFor >= config.backgroundGraceMs) {
            end();
            start();
            return;
        }
        isVisible = true;
    }
    function end() {
        if (visitId === null || visitEnded)
            return;
        visitEnded = true;
        isVisible = false;
        hiddenAt = null;
        clearIdleTimer();
        clearGraceTimer();
        options.onInactive({ visitId });
    }
    function dispose() {
        clearIdleTimer();
        clearGraceTimer();
    }
    return {
        start,
        registerInteraction,
        hide,
        show,
        end,
        dispose,
        currentVisitId: () => visitId,
    };
}
